import React, { useState } from 'react';
import get from 'lodash.get';
import { I18n } from 'react-redux-i18n';

import { useAppSelector } from '../../../../hooks/redux/useApp';
import { AnnouncementBanner } from '../../../../components/common/announcementBanner';
import { getActiveProgramAnnouncement } from '../../helpers/announcement';
import {
  dismissAnnouncement,
  getAnnouncementSignature,
  isAnnouncementDismissed
} from '../../helpers/announcementDismissal';

/**
 * The event program's announcement, shown to a charity at the top of its
 * PlaceHub program page. Reads the parent event program that comes nested in
 * the campaign program record the page already loads, so there is no extra
 * request. Renders nothing until that record is in or when no announcement is
 * switched on. A charity can dismiss it, and it stays dismissed on that device
 * until a system admin changes the announcement.
 */
export const ProgramAnnouncementBanner = () => {
  const eventAmbassadorProgram: unknown = useAppSelector((state) =>
    get(
      state,
      'program.campaignAmbassadorProgramRecord.data.data.eventAmbassadorProgram'
    )
  );
  // Local storage is not reactive, so this hides the banner the moment it is
  // dismissed rather than on the next render that happens to re-read it.
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(
    null
  );
  const announcement = getActiveProgramAnnouncement(eventAmbassadorProgram);
  const programIdValue: unknown = get(eventAmbassadorProgram, 'id');
  const programId =
    typeof programIdValue === 'number' || typeof programIdValue === 'string'
      ? String(programIdValue)
      : null;

  if (
    !announcement ||
    dismissedSignature === getAnnouncementSignature(announcement) ||
    (programId !== null && isAnnouncementDismissed(programId, announcement))
  ) {
    return null;
  }

  const onDismiss = () => {
    if (programId !== null) {
      dismissAnnouncement(programId, announcement);
    }
    setDismissedSignature(getAnnouncementSignature(announcement));
  };

  return (
    <AnnouncementBanner
      testId="program-announcement"
      title={announcement.title}
      body={announcement.description}
      link={announcement.callToAction}
      onDismiss={onDismiss}
      dismissLabel={I18n.t('programs.template.page.announcements.dismiss')}
      dismissTestId="program-announcement-dismiss"
    />
  );
};

export default ProgramAnnouncementBanner;
